import { Channel, SubscriptionPrice } from 'api/admin/models';
import { del, get, patch, post, put } from './client';
import { buildPath, toJson } from './utils';
import { ADMIN_ORGANIZATIONS_PATH, API_ADMIN_URL } from './constants';
import { GeneralListType, IdType, Options } from './types';
import {
  AdminEventCCRequestStatus,
  DefaultLanguageEventDisplay,
  EventCreate,
  EventDisplayList,
  EventStatistics,
  EventUpdate,
  HideEventQuestion,
  OrganizationAdminDetail,
  OrganizationAdminList,
  OrganizationAdminShortList,
  OrganizationDashboardStatistics,
  OrganizationImage,
  OrganizationManyTimeQuestionDisplay,
  OrganizationSettings,
  OrganizationType,
  OrganizationVideoDisplay,
  PresignedUrl,
  Report,
} from './admin/models';

const EVENTS = 'events';
const ALL_ORGANIZATIONS = 'all-organizations';
const PUBLISH = 'publish';
const MANY_TIME_QUESTIONS = 'many-time-questions';
const EXPORT_ORDERS_CSV = 'export-orders-csv-v3';
const IMAGES = 'images';
const VIDEOS = 'videos';
const SAVE_AS_DRAFT = 'save-as-draft';
const CANCEL = 'cancel';
const ARCHIVE = 'archive';
const UNARCHIVE = 'unarchive';
const MARK_AS_TEST = 'mark-as-test';
const STRIPE_CONNECT = 'stripe-connect';
const STRIPE_OAUTH = 'stripe-oauth';
const STRIPE_EXPRESS_DASHBOARD_LINK = 'stripe-express-dashboard-link';
const CLONE = 'clone';
const ORDERS_TRANSACTIONS_CSV = 'export-orders-transactions-csv-v3';
const ORDERS_TICKETS_CSV = 'export-orders-tickets-csv-v3';
const ICS_FEED_LINK_GENERATOR = 'ics-feed-link-generator';
const ORGANIZATION_TYPES = 'organization-types';
const SETTINGS = 'settings';
const HIDE_QUESTION = 'hide-question';
const STATISTICS = 'statistics';
const REPORTS = 'reports';
const REQUEST_LINK = 'request-link';
const CC_MARK_AS_APPROVE = 'cc-mark-as-approve';
const EVENTS_AUTOCOMPLETE = 'events-autocomplete-search';
const SPECIFIC_EVENT = 'specific-event';
const SPREEDLY_CONNECT_MONERIS = 'spreedly-connect-moneris';
const SPREEDLY_CONNECT_STRIPE = 'spreedly-connect-stripe';
const SPREEDLY_CONNECT_AUTHORIZE_NET = 'spreedly-connect-authorize-net';
const SPREEDLY_DISCONNECT_GATEWAY = 'spreedly-disconnect-gateway';
const STRIPE_DISCONNECT = 'stripe-disconnect';
const CHANNELS = 'channels';
const PARENT = 'parent';
const SUBSCRIPTION = 'subscription';
const SUBSCRIPTIONS = 'subscriptions';
const PRICES = 'prices';
const STRIPE = 'stripe';
const BILLING_PORTAL_SESSION = 'billing-portal-session';

export const getOrganizations = (options: any): Promise<GeneralListType<OrganizationAdminList>> =>
  get(ADMIN_ORGANIZATIONS_PATH, options).then(toJson);

export const createOrganization = (options: any): Promise<OrganizationAdminShortList> =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH), options).then(toJson);

export const getAllOrganizations = (options: any): Promise<OrganizationAdminShortList> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, ALL_ORGANIZATIONS), options).then(toJson);

export const getOrganization = (organizationId: IdType, options: any): Promise<OrganizationAdminDetail> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId), options).then(toJson);

export const partialUpdateOrganization = (organizationId: IdType, options: any): Promise<OrganizationType> =>
  patch(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId), options).then(toJson);

export const getImages = (organizationId: IdType, options: any): Promise<OrganizationImage> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, IMAGES), options).then(toJson);

export const createImage = (organizationId: IdType, options: any): Promise<OrganizationImage> =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, IMAGES), options).then(toJson);

export const updateImage = (organizationId: IdType, imageId: IdType, options: any): Promise<OrganizationImage> =>
  put(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, IMAGES, imageId), options).then(toJson);

export const deleteImage = (organizationId: IdType, imageId: IdType, options: any) =>
  del(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, IMAGES, imageId), options);

export const getVideos = (organizationId: IdType, options: any): Promise<OrganizationVideoDisplay> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, VIDEOS), options).then(toJson);

export const createVideo = (organizationId: IdType, options: any): Promise<OrganizationVideoDisplay> =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, VIDEOS), options).then(toJson);

export const updateVideo = (organizationId: IdType, videoId: IdType, options: any): Promise<OrganizationVideoDisplay> =>
  put(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, VIDEOS, videoId), options).then(toJson);

export const deleteVideo = (organizationId: IdType, videoId: IdType, options: any) =>
  del(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, VIDEOS, videoId), options);

export const getEvents = (organizationId: IdType, options: any): Promise<GeneralListType<EventDisplayList>> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS), options).then(toJson);

export const getOrgEventsAutocomplete = (
  organizationId: IdType,
  options: any,
): Promise<GeneralListType<EventDisplayList>> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS_AUTOCOMPLETE), options).then(toJson);

export const getEvent = (organizationId: IdType, eventId: IdType, options: any): Promise<DefaultLanguageEventDisplay> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId), options).then(toJson);

export const createEvent = (organizationId: IdType, options: any): Promise<EventCreate> =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS), options).then(toJson);

export const partialUpdateEvent = (
  organizationId: IdType,
  eventId: IdType,
  options: Options<EventUpdate>,
): Promise<DefaultLanguageEventDisplay> =>
  patch(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId), options).then(toJson);

export const deleteEvent = (organizationId: IdType, eventId: IdType, options: any) =>
  del(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId), options);

export const cloneAdminEvent = (organizationId: IdType, eventId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, CLONE), options).then(toJson);

export const publishEvent = (organizationId: IdType, eventId: IdType, options: any): Promise<any> =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, PUBLISH), options).then(toJson);

export const saveEventAsDraft = (organizationId: IdType, eventId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, SAVE_AS_DRAFT), options).then(toJson);

export const cancelEvent = (organizationId: IdType, eventId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, CANCEL), options).then(toJson);

export const archiveEvent = (organizationId: IdType, eventId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, ARCHIVE), options).then(toJson);

export const unarchiveEvent = (organizationId: IdType, eventId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, UNARCHIVE), options).then(toJson);

// SEPARATE TYPE
export const markAsTestEvent = (organizationId: IdType, eventId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, MARK_AS_TEST), options).then(toJson);

export const getManyTimeQuestions = (
  organizationId: IdType,
  options: any,
): Promise<GeneralListType<OrganizationManyTimeQuestionDisplay>> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, MANY_TIME_QUESTIONS), options).then(toJson);

export const getManyTimeQuestion = (
  organizationId: IdType,
  questionId: IdType,
  options: any,
): Promise<OrganizationManyTimeQuestionDisplay> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, MANY_TIME_QUESTIONS, questionId), options).then(toJson);

export const createManyTimeQuestion = (
  organizationId: IdType,
  options: any,
): Promise<OrganizationManyTimeQuestionDisplay> =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, MANY_TIME_QUESTIONS), options).then(toJson);

export const updateManyTimeQuestion = (
  organizationId: IdType,
  questionId: IdType,
  options: any,
): Promise<OrganizationManyTimeQuestionDisplay> =>
  put(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, MANY_TIME_QUESTIONS, questionId), options).then(toJson);

export const deleteManyTimeQuestion = (organizationId: IdType, questionId: IdType, options: any) =>
  del(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, MANY_TIME_QUESTIONS, questionId), options);

// SEPARATE TYPE
export const requestStripeConnect = (options: any) =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, STRIPE_CONNECT), options).then(toJson);

// SEPARATE TYPE
export const requestOauthAuthorization = (organizationId: IdType, options: any) =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, STRIPE_OAUTH), options).then(toJson);

// SEPARATE TYPE
export const getStripeExpressDashboardLink = (organizationId: IdType, options: any) =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, STRIPE_EXPRESS_DASHBOARD_LINK), options).then(toJson);

export const getOrdersCsv = (organizationId: IdType, eventId: IdType, options: any): Promise<Response> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, EXPORT_ORDERS_CSV), options);

export const getOrderTransactionsCsv = (organizationId: IdType, eventId: IdType, options: any): Promise<Response> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, ORDERS_TRANSACTIONS_CSV), options);

export const getOrderTicketsCsv = (organizationId: IdType, eventId: IdType, options: any): Promise<Response> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, ORDERS_TICKETS_CSV), options);

// SEPARATE TYPE
export const getIcsFeedLink = (organizationId: IdType, options: any) =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, ICS_FEED_LINK_GENERATOR), options).then(toJson);

export const getOrganizationTypes = (options: any): Promise<OrganizationType> =>
  get(buildPath(API_ADMIN_URL, ORGANIZATION_TYPES), options).then(toJson);

export const getAdminOrganizationSetting = (organizationId: IdType, options: any): Promise<OrganizationSettings> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, SETTINGS), options).then(toJson);

export const getAdminOrganizationSettingSpecificEvent = (
  organizationId: IdType,
  options: any,
): Promise<OrganizationSettings> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, SETTINGS, SPECIFIC_EVENT), options).then(toJson);

export const getAdminEventStatistics = (
  organizationId: IdType,
  eventId: IdType,
  options: any,
): Promise<EventStatistics> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, STATISTICS), options).then(toJson);

export const updateAdminOrganizationSetting = (organizationId: IdType, options: any): Promise<OrganizationSettings> =>
  patch(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, SETTINGS), options).then(toJson);

export const hideAdminQuestion = (organizationId: IdType, eventId: IdType, options: any): Promise<HideEventQuestion> =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, HIDE_QUESTION), options).then(toJson);

export const createAdminOrganizationReport = (organizationId: IdType, options: any): Promise<Report> =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, REPORTS), options).then(toJson);

export const getAdminOrganizationReport = (organizationId: IdType, options: any): Promise<GeneralListType<Report>> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, REPORTS), options).then(toJson);

export const getOrganizationReportLink = (
  organizationId: IdType,
  reportId: IdType,
  options: any,
): Promise<PresignedUrl> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, REPORTS, reportId, REQUEST_LINK), options).then(toJson);

export const getAdminOrganizationStatistics = (
  organizationId: IdType,
  options: any,
): Promise<OrganizationDashboardStatistics> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, STATISTICS), options).then(toJson);

export const updateEventCCStatus = (
  organizationId: IdType,
  eventId: IdType,
  options: any,
): Promise<AdminEventCCRequestStatus> =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, EVENTS, eventId, CC_MARK_AS_APPROVE), options).then(toJson);

export const connectSpreedlyMoneris = (organizationId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, SPREEDLY_CONNECT_MONERIS), options).then((resp) =>
    resp.bodyUsed ? toJson(resp) : {},
  );

export const connectSpreedlyStripe = (organizationId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, SPREEDLY_CONNECT_STRIPE), options).then((resp) =>
    resp.bodyUsed ? toJson(resp) : {},
  );

export const connectSpreedlyAuthorizeNet = (organizationId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, SPREEDLY_CONNECT_AUTHORIZE_NET), options).then((resp) =>
    resp.bodyUsed ? toJson(resp) : {},
  );

export const disconnectSpreedly = (organizationId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, SPREEDLY_DISCONNECT_GATEWAY), options).then((resp) =>
    resp ? toJson(resp) : null,
  );

export const disconnectStripe = (organizationId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, STRIPE_DISCONNECT), options).then((resp) =>
    resp ? toJson(resp) : null,
  );

export const connectSubscription = (organizationId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, SUBSCRIPTIONS), options).then((resp) =>
    resp.bodyUsed ? toJson(resp) : {},
  );

export const sessionSubscription = (organizationId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, STRIPE, BILLING_PORTAL_SESSION), options).then(toJson);

export const getAdminOrganizationChannels = (organizationId: IdType, options: any): Promise<GeneralListType<Channel>> =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, CHANNELS), options).then(toJson);

export const createAdminOrganizationChannel = (organizationId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, CHANNELS), options).then(toJson);

export const deleteAdminOrganizationChannel = (organizationId: IdType, channelId: IdType, options: any) =>
  del(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, CHANNELS, channelId), options);

export const getParent = (organizationId: IdType, options: any) =>
  get(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, PARENT), options).then(toJson);

export const createParent = (organizationId: IdType, options: any) =>
  post(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, PARENT), options).then(toJson);

export const deleteParent = (organizationId: IdType, options: any) =>
  del(buildPath(ADMIN_ORGANIZATIONS_PATH, organizationId, PARENT), options);

export const getSubscriptionPrices = (options: any): Promise<GeneralListType<SubscriptionPrice>> =>
  get(buildPath(API_ADMIN_URL, SUBSCRIPTION, PRICES), options).then(toJson);
